import { z } from 'zod/v4'

type EnvOptions<TSchema extends z.core.$ZodShape> = {
  schema: TSchema
  skipValidation?: boolean
}

export function createEnv<TSchema extends z.core.$ZodShape>(
  options: EnvOptions<TSchema>
): Readonly<z.infer<z.ZodObject<TSchema>>> {
  const {
    schema,
    skipValidation = process.env['SKIP_ENV_VALIDATION'] === 'true',
  } = options

  if (skipValidation) {
    return process.env as unknown as z.infer<z.ZodObject<TSchema>>
  }

  const parsed = z.object(schema).safeParse(process.env)

  if (!parsed.success) {
    const formatted = parsed.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n')

    throw new Error(`❌ Invalid environment variables:\n${formatted}`)
  }

  return parsed.data
}

type ErrorOption = { error?: string }

export const envString = (options?: ErrorOption) =>
  z.string().min(1, { error: options?.error ?? 'Required' })

export const envBoolean = (options?: ErrorOption) =>
  z.stringbool({ error: options?.error })

export const envNumber = (options?: ErrorOption) =>
  z.coerce.number({ error: options?.error })

export const envUrl = (options?: ErrorOption) =>
  z.url({ error: options?.error })

export { z }
